// Content Moderation & AI Filtering System
// Protects minors from inappropriate content in messaging and music

export interface ModerationResult {
  allowed: boolean;
  reason?: string;
  severity: 'none' | 'low' | 'medium' | 'high' | 'severe';
  flaggedTerms?: string[];
  suggestedAction?: 'warn' | 'block' | 'review' | 'report';
}

export interface UserContext {
  ageGroup: 'under13' | 'teen' | 'adult';
  userId: string;
}

// Profanity and inappropriate content patterns
const PROFANITY_PATTERNS = [
  // Explicit profanity (masked for code)
  /\b(f[u\*]ck|sh[i\*]t|b[i\*]tch|d[a\*]mn|h[e\*]ll|a[s\*]s|c[u\*]nt|d[i\*]ck|p[i\*]ss)\b/gi,
  /\b(bastard|whore|slut|fag|n[i\*]gga|n[i\*]gger)\b/gi,
];

// Sexual content patterns
const SEXUAL_PATTERNS = [
  /\b(sex|sexual|porn|nude|naked|boobs|penis|vagina|dick|pussy|cock)\b/gi,
  /\b(horny|masturbat|orgasm|cum|erotic|xxx|nsfw)\b/gi,
];

// Violence and threats
const VIOLENCE_PATTERNS = [
  /\b(kill|murder|rape|assault|attack|stab|shoot|gun|weapon|bomb)\b/gi,
  /\b(hurt|harm|die|death|suicide|hang yourself|jump off)\b/gi,
  /\b(i will kill|gonna kill|wanna kill|beat you up)\b/gi,
];

// Bullying and harassment
const BULLYING_PATTERNS = [
  /\b(ugly|stupid|idiot|loser|worthless|fat|dumb|retard)\b/gi,
  /\b(kill yourself|kys|nobody likes you|everyone hates you)\b/gi,
  /\b(you should die|go die|unalive|end it)\b/gi,
];

// Drug and alcohol references
const SUBSTANCE_PATTERNS = [
  /\b(weed|marijuana|cocaine|heroin|meth|drugs|high|stoned|drunk|alcohol|beer|vodka)\b/gi,
  /\b(smoke|vape|pills|xanax|molly|ecstasy|acid|lsd)\b/gi,
];

// Personal information (PII) patterns
const PII_PATTERNS = [
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, // Phone numbers
  /\b\d{3}[-]?\d{2}[-]?\d{4}\b/g, // SSN
  /\b\d{1,5}\s\w+\s(street|st|avenue|ave|road|rd|drive|dr|lane|ln)\b/gi, // Addresses
];

// Grooming and predatory behavior
const GROOMING_PATTERNS = [
  /\b(meet me|come over|my place|where do you live|send me a pic|send nudes)\b/gi,
  /\b(secret|don't tell|between us|our secret|special friend)\b/gi,
  /\b(age|how old|asl|cam|webcam|skype|discord|snap|snapchat)\b/gi,
];

/**
 * Main content moderation function
 */
export function moderateContent(
  content: string,
  userContext: UserContext
): ModerationResult {
  const trimmedContent = content.trim().toLowerCase();
  
  // Empty messages are allowed
  if (!trimmedContent) {
    return { allowed: true, severity: 'none' };
  }

  const flaggedTerms: string[] = [];
  let highestSeverity: ModerationResult['severity'] = 'none';
  let suggestedAction: ModerationResult['suggestedAction'] = undefined;

  // Check for PII (always block for minors)
  if (userContext.ageGroup !== 'adult') {
    const piiMatches = PII_PATTERNS.some(pattern => pattern.test(content));
    if (piiMatches) {
      return {
        allowed: false,
        reason: 'Personal information detected. For your safety, please do not share phone numbers, addresses, or other personal details.',
        severity: 'severe',
        suggestedAction: 'block',
        flaggedTerms: ['personal information']
      };
    }
  }

  // Check for grooming patterns (CRITICAL - always block for minors)
  if (userContext.ageGroup !== 'adult') {
    for (const pattern of GROOMING_PATTERNS) {
      const matches = content.match(pattern);
      if (matches) {
        return {
          allowed: false,
          reason: 'This message contains potentially unsafe content. Please contact a trusted adult if you feel uncomfortable.',
          severity: 'severe',
          suggestedAction: 'report',
          flaggedTerms: matches
        };
      }
    }
  }

  // Check for violence and threats
  for (const pattern of VIOLENCE_PATTERNS) {
    const matches = content.match(pattern);
    if (matches) {
      flaggedTerms.push(...matches);
      highestSeverity = 'severe';
      suggestedAction = 'report';
      
      if (userContext.ageGroup !== 'adult') {
        return {
          allowed: false,
          reason: 'This message contains threatening or violent content and cannot be sent.',
          severity: 'severe',
          suggestedAction: 'block',
          flaggedTerms
        };
      }
    }
  }

  // Check for sexual content
  for (const pattern of SEXUAL_PATTERNS) {
    const matches = content.match(pattern);
    if (matches) {
      flaggedTerms.push(...matches);
      
      if (userContext.ageGroup === 'under13') {
        return {
          allowed: false,
          reason: 'This message contains inappropriate content for your age group.',
          severity: 'severe',
          suggestedAction: 'block',
          flaggedTerms
        };
      } else if (userContext.ageGroup === 'teen') {
        return {
          allowed: false,
          reason: 'This message contains mature content that is not allowed.',
          severity: 'high',
          suggestedAction: 'block',
          flaggedTerms
        };
      }
      
      highestSeverity = 'high';
    }
  }

  // Check for profanity
  for (const pattern of PROFANITY_PATTERNS) {
    const matches = content.match(pattern);
    if (matches) {
      flaggedTerms.push(...matches);
      
      if (userContext.ageGroup === 'under13') {
        return {
          allowed: false,
          reason: 'Please use appropriate language.',
          severity: 'medium',
          suggestedAction: 'warn',
          flaggedTerms
        };
      } else if (userContext.ageGroup === 'teen') {
        highestSeverity = 'low';
        suggestedAction = 'warn';
      }
    }
  }

  // Check for bullying
  for (const pattern of BULLYING_PATTERNS) {
    const matches = content.match(pattern);
    if (matches) {
      flaggedTerms.push(...matches);
      highestSeverity = 'high';
      
      if (userContext.ageGroup !== 'adult') {
        return {
          allowed: false,
          reason: 'This message contains bullying or harassment and cannot be sent. Be kind to others!',
          severity: 'high',
          suggestedAction: 'block',
          flaggedTerms
        };
      }
    }
  }

  // Check for substance references
  if (userContext.ageGroup !== 'adult') {
    for (const pattern of SUBSTANCE_PATTERNS) {
      const matches = content.match(pattern);
      if (matches) {
        flaggedTerms.push(...matches);
        
        return {
          allowed: false,
          reason: 'This message contains references to substances that are inappropriate for minors.',
          severity: 'high',
          suggestedAction: 'block',
          flaggedTerms
        };
      }
    }
  }

  // If we got here, the content is allowed but may have warnings
  if (flaggedTerms.length > 0) {
    return {
      allowed: true,
      severity: highestSeverity,
      suggestedAction,
      flaggedTerms,
      reason: 'Message contains flagged content but is allowed for your age group.'
    };
  }

  return { allowed: true, severity: 'none' };
}

/**
 * Filter music content based on age group
 */
export function filterMusicForAge(
  songTitle: string,
  artist: string,
  ageGroup: 'under13' | 'teen' | 'adult'
): ModerationResult {
  const combinedText = `${songTitle} ${artist}`.toLowerCase();
  
  // Adults get all content
  if (ageGroup === 'adult') {
    return { allowed: true, severity: 'none' };
  }

  // Check for explicit content indicators
  const explicitIndicators = [
    /explicit/i,
    /\(parental advisory\)/i,
    /\[explicit\]/i,
  ];

  for (const pattern of explicitIndicators) {
    if (pattern.test(combinedText)) {
      return {
        allowed: false,
        reason: 'This song contains explicit content.',
        severity: 'high',
        suggestedAction: 'block'
      };
    }
  }

  // Check for inappropriate terms in title/artist
  for (const pattern of [...PROFANITY_PATTERNS, ...SEXUAL_PATTERNS]) {
    if (pattern.test(combinedText)) {
      return {
        allowed: false,
        reason: 'This song may contain inappropriate content for your age group.',
        severity: 'medium',
        suggestedAction: 'block'
      };
    }
  }

  return { allowed: true, severity: 'none' };
}

/**
 * Log moderation action for review
 */
export async function logModerationAction(
  userId: string,
  content: string,
  result: ModerationResult,
  context: string
): Promise<void> {
  // In production, this would log to a database for admin review
  console.log('[MODERATION]', {
    timestamp: new Date().toISOString(),
    userId,
    context,
    severity: result.severity,
    action: result.suggestedAction,
    flaggedTerms: result.flaggedTerms,
    contentPreview: content.substring(0, 100)
  });

  // For severe cases, could trigger email alerts to admins
  if (result.severity === 'severe' && result.suggestedAction === 'report') {
    console.warn('[MODERATION ALERT] Severe content flagged:', {
      userId,
      context,
      timestamp: new Date().toISOString()
    });
  }
}
